// Peak-power-style curves and time-in-zone breakdowns for any metric.

export const PEAK_DURATIONS_SECONDS = [
  5, 10, 12, 20, 30,
  60, 120, 300, 360, 600, 720, 1200, 1800,
  3600, 5400, 7200, 10800, 14400, 18000,
];

export function fmtDurationLabel(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "";
  const s = Number(seconds);
  if (s < 60) return `${s} sec`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const hrs = s / 3600;
  return `${hrs % 1 === 0 ? hrs : hrs.toFixed(1)} hr`;
}

export function fmtMinutes(seconds) {
  const total = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function applicableDurations(totalSeconds) {
  const limit = Math.max(0, totalSeconds || 0);
  return PEAK_DURATIONS_SECONDS.filter((d) => d <= limit);
}

function timeOriginMs(samples, activityStartTime) {
  if (activityStartTime) {
    const ms = new Date(activityStartTime).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  if (samples[0]?.t) {
    const ms = new Date(samples[0].t).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

/**
 * Aligns a metric to a 1-Hz sample array using timestamps when available.
 * Forward-fills nulls so brief sensor drops don't create false low peaks.
 */
export function build1HzSeries(samples, key, activityStartTime, transform = null) {
  if (!samples?.length) return { series: [], totalSec: 0 };
  const origin = timeOriginMs(samples, activityStartTime);
  const lastT = samples[samples.length - 1]?.t
    ? new Date(samples[samples.length - 1].t).getTime()
    : null;
  const totalSec =
    origin != null && lastT != null
      ? Math.max(0, Math.round((lastT - origin) / 1000))
      : samples.length - 1;

  const series = new Array(totalSec + 1).fill(null);
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i];
    const ts = s.t ? new Date(s.t).getTime() : null;
    const sec =
      origin != null && ts != null ? Math.round((ts - origin) / 1000) : i;
    if (sec < 0 || sec > totalSec) continue;
    const raw = s[key];
    if (raw != null && !Number.isNaN(raw)) {
      series[sec] = transform ? transform(Number(raw)) : Number(raw);
    }
  }

  let last = null;
  for (let i = 0; i < series.length; i += 1) {
    if (series[i] == null) series[i] = last;
    else last = series[i];
  }
  return { series, totalSec };
}

/** Peak (best rolling avg) over each duration in `durations`. */
export function computePeakCurve(series, durations, opts = {}) {
  const { floorBest = 0 } = opts;
  if (!series?.length) return [];
  const values = series.map((v) => (v == null || Number.isNaN(v) ? 0 : v));
  const n = values.length;
  const cum = new Array(n + 1).fill(0);
  for (let i = 0; i < n; i += 1) cum[i + 1] = cum[i] + values[i];

  return durations
    .filter((d) => d <= n)
    .map((w) => {
      let best = floorBest;
      for (let i = 0; i + w <= n; i += 1) {
        const avg = (cum[i + w] - cum[i]) / w;
        if (avg > best) best = avg;
      }
      return { seconds: w, value: best };
    });
}

/** Time (seconds) spent in each zone. Bounds must be sorted ascending. */
export function computeZoneBreakdown(series, bounds) {
  const zoneCount = bounds.length - 1;
  const secs = new Array(zoneCount).fill(0);
  for (const v of series) {
    if (v == null) continue;
    for (let i = 0; i < zoneCount; i += 1) {
      if (v >= bounds[i] && v < bounds[i + 1]) {
        secs[i] += 1;
        break;
      }
    }
  }
  return secs;
}

// Zone bounds ------------------------------------------------------------

export function powerZoneBounds(ftp) {
  if (ftp && ftp > 0) {
    return [0, 0.55 * ftp, 0.75 * ftp, 0.9 * ftp, 1.05 * ftp, 1.2 * ftp, Infinity];
  }
  return [0, 100, 150, 200, 250, 300, Infinity];
}

export const POWER_ZONE_NAMES = [
  "Recovery",
  "Endurance",
  "Tempo",
  "Threshold",
  "VO2 Max",
  "Anaerobic",
];
export const POWER_ZONE_COLORS = [
  "#5AC8FA",
  "#31B978",
  "#FFCC00",
  "#FF9500",
  "#FF3B30",
  "#BF5AF2",
];

export function hrZoneBounds(maxHr) {
  const mh = maxHr && maxHr > 0 ? maxHr : 190;
  return [0, 0.6 * mh, 0.7 * mh, 0.8 * mh, 0.9 * mh, mh, Infinity];
}
export const HR_ZONE_NAMES = ["Z1", "Z2", "Z3", "Z4", "Z5", "Z5+"];
export const HR_ZONE_COLORS = [
  "#5AC8FA",
  "#31B978",
  "#FFCC00",
  "#FF9500",
  "#FF3B30",
  "#8B0000",
];

export const CADENCE_ZONE_BOUNDS = [0, 60, 75, 85, 95, 105, Infinity];
export const CADENCE_ZONE_NAMES = ["<60", "60-75", "75-85", "85-95", "95-105", "105+"];
export const CADENCE_ZONE_COLORS = [
  "#5AC8FA",
  "#31B978",
  "#FFCC00",
  "#FF9500",
  "#FF3B30",
  "#BF5AF2",
];

export const SPEED_ZONE_BOUNDS_KMH = [0, 10, 20, 25, 30, 35, Infinity];
export const SPEED_ZONE_NAMES = ["<10", "10-20", "20-25", "25-30", "30-35", "35+"];
export const SPEED_ZONE_COLORS = [
  "#5AC8FA",
  "#31B978",
  "#FFCC00",
  "#FF9500",
  "#FF3B30",
  "#BF5AF2",
];

export const TEMPERATURE_ZONE_BOUNDS = [-50, 0, 10, 15, 20, 25, 30, Infinity];
export const TEMPERATURE_ZONE_NAMES = ["<0", "0-10", "10-15", "15-20", "20-25", "25-30", "30+"];
export const TEMPERATURE_ZONE_COLORS = [
  "#2F8EEA",
  "#5AC8FA",
  "#31B978",
  "#FFCC00",
  "#FF9500",
  "#FF3B30",
  "#BF5AF2",
];

export function labelBounds(bounds, unit) {
  const rows = [];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    const lo = Math.round(bounds[i]);
    const hi = bounds[i + 1];
    rows.push(
      hi === Infinity ? `${lo}+ ${unit}` : `${lo}-${Math.round(hi)} ${unit}`
    );
  }
  return rows;
}
